<template>
    <div class="floating-chat">
        <button class="floating-chat__trigger"
                type="button"
                aria-label="Åbn chat"
                :disabled="creating"
                @click="toggle">
            <CIcon name="chat" height="26" width="26" fill/>
            <span v-if="hasUnreadMessages" class="floating-chat__badge">{{ unreadCount }}</span>
        </button>

        <Transition name="floating-chat-slide">
            <div v-if="isOpen" class="floating-chat__panel">
                <header class="floating-chat__header">
                    <span class="floating-chat__title">Support Chat</span>
                    <button class="floating-chat__close" type="button" @click="close">
                        <CIcon name="close" height="18" width="18" fill/>
                    </button>
                </header>
                <div class="floating-chat__body">
                    <!-- Contact prompt for guests without a saved name/email -->
                    <div v-if="showContactPrompt" class="flex flex-col">
                        <div class="p-6">
                            <h3 class="text-16 font-semibold mb-3">
                                Velkommen til chatten
                            </h3>
                            <p class="text-14 mb-4">
                                Angiv venligst dit navn og din e-mail for at fortsætte.
                            </p>
                            <InputText
                                ref="namePromptInput"
                                v-model="tempGuestName"
                                name="guestName"
                                label="Dit navn"
                                placeholder="Skriv dit navn"
                                rules="min:2"
                                :disabled="savingName"/>
                            <InputText
                                v-model="tempGuestEmail"
                                name="guestEmail"
                                type="email"
                                label="Din e-mail"
                                placeholder="Skriv din e-mail"
                                :disabled="savingName"
                                rules="email"
                                class="mt-4"/>
                        </div>
                        <div class="p-6 pt-0">
                            <CallToAction
                                type="button"
                                class="text-14 w-full"
                                :disabled="!canSaveContact"
                                variant="blue"
                                :loading="savingName"
                                @click="submitContact">
                                Fortsæt
                            </CallToAction>
                        </div>
                    </div>

                    <!-- Chat component - only show when name is set or user is logged in -->
                    <CaseChat
                        v-else-if="activeChatId"
                        :chat-id="activeChatId"
                        :guest-name="guestName"
                        :guest-email="guestEmail"
                        class="floating-chat__chat"/>
                    <div v-else class="floating-chat__status">
                        <span v-if="creating">Åbner chat...</span>
                        <span v-else>Kunne ikke åbne chatten</span>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import CaseChat from '@/core/components/chat/CaseChat.vue';
import communicationApiService from '@/core/api/controllers/communicationApi.service';
import { GuestChatStorage, clearGuestChat, loadGuestChat, saveGuestChat } from '@/core/chat/guestChat.storage';
import notificationsService from '@/core/notifications/notifications.service';
import { userStore } from '@/core/store/user/user.store';
import serverHubClient from '@/core/signalr/serverHub.client';
import { useBusEvents } from '@/core/bus';
import InputText from '@/core/forms/inputs/InputText.vue';

const isOpen = ref(false);
const creating = ref(false);
const guestChat = ref<GuestChatStorage | null>(null);
const registeredChatId = ref<string | null>(null);
const unreadCount = ref(0);
const isRegistering = ref(false);
const tempGuestName = ref('');
const tempGuestEmail = ref('');
const savingName = ref(false);
const namePromptInput = ref<InstanceType<typeof InputText> | null>(null);

const user = userStore.user;

async function validateGuestChat(chatId: string): Promise<boolean> {
    const result = await communicationApiService.getMessagesForChat(chatId, 0, 0, { suppressErrors: true, hideLoadingBar: true });

    return !!result;
}

onMounted(async() => {
    const stored = loadGuestChat();
    if (stored) {
        guestChat.value = stored;
        tempGuestName.value = stored.name;
        tempGuestEmail.value = stored.email;

        const chatIsValid = await validateGuestChat(stored.chatId);
        if (!chatIsValid) {
            guestChat.value = null;
        }
    }
});

watch(user, newUser => {
    if (newUser && guestChat.value) {
        guestChat.value = null;
        clearGuestChat();
    }

    if (newUser) {
        void unregisterGuestChat();
        isOpen.value = false;
        unreadCount.value = 0;
    }
});

watch(guestChat, value => {
    if (value && !user.value) {
        saveGuestChat(value);
        if (showContactPrompt.value) {
            tempGuestName.value = value.name;
            tempGuestEmail.value = value.email;
        }
    }
    if (!value) {
        clearGuestChat();
        unreadCount.value = 0;
        tempGuestName.value = '';
        tempGuestEmail.value = '';
        void unregisterGuestChat();
    }
});

const activeChatId = computed(() => user.value?.groupChatId ?? guestChat.value?.chatId ?? null);
const guestName = computed(() => user.value ? null : guestChat.value?.name ?? null);
const guestEmail = computed(() => user.value ? null : guestChat.value?.email ?? null);
const normalizedGuestName = computed(() => guestName.value?.trim().toLowerCase() ?? '');
const hasUnreadMessages = computed(() => unreadCount.value > 0);
const showContactPrompt = computed(() => !user.value && activeChatId.value && (!guestName.value || !guestEmail.value));
const isEmailValid = computed(() => {
    const value = tempGuestEmail.value.trim();
    if (!value)
        return false;

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
});
const canSaveContact = computed(() => !!tempGuestName.value.trim() && isEmailValid.value);

async function submitContact() {
    const name = tempGuestName.value.trim();
    const email = tempGuestEmail.value.trim();
    if (!name || !isEmailValid.value || savingName.value) {
        return;
    }

    savingName.value = true;

    if (!guestChat.value && activeChatId.value) {
        guestChat.value = {
            chatId: activeChatId.value,
            name,
            email,
        };
    } else if (guestChat.value) {
        guestChat.value = {
            ...guestChat.value,
            name,
            email,
        };
    }

    savingName.value = false;
    tempGuestName.value = '';
    tempGuestEmail.value = '';
}

async function ensureGuestChat() {
    if (activeChatId.value || creating.value || user.value) {
        return;
    }

    creating.value = true;
    const result = await communicationApiService.createGuestChat();
    creating.value = false;

    if (!result) {
        notificationsService.notify({
            title: 'Chat',
            message: 'Kunne ikke oprette chat. Prøv igen senere.',
            type: 'Error',
        });
        return;
    }

    guestChat.value = {
        chatId: result.id,
        name: '',
        email: '',
    };
    unreadCount.value = 0;
}

async function toggle() {
    if (isOpen.value) {
        close();
        return;
    }

    if (!activeChatId.value) {
        await ensureGuestChat();
    }

    if (activeChatId.value) {
        isOpen.value = true;
        unreadCount.value = 0;
        
        // Focus the name input if prompt is shown
        if (showContactPrompt.value) {
            await nextTick();
            namePromptInput.value?.focusInput();
        }
    }
}

function close() {
    isOpen.value = false;
    unreadCount.value = 0;
    tempGuestName.value = '';
}

async function registerGuestChat(chatId: string, force = false) {
    if (!force && registeredChatId.value === chatId) {
        return;
    }

    if (isRegistering.value) {
        return;
    }

    isRegistering.value = true;
    try {
        await serverHubClient.start();
        await serverHubClient.registerGuestChat(chatId);
        registeredChatId.value = chatId;
    } catch (error) {
        console.error(error);
        notificationsService.notify({
            title: 'Chat',
            message: 'Kunne ikke forbinde til chatten. Prøv igen senere.',
            type: 'Error',
        });
    } finally {
        isRegistering.value = false;
    }
}

async function unregisterGuestChat(chatId?: string) {
    if (!registeredChatId.value) {
        return;
    }

    const targetChatId = chatId ?? registeredChatId.value;

    if (registeredChatId.value !== targetChatId) {
        return;
    }

    try {
        if (serverHubClient.isConnected.value) {
            await serverHubClient.unregisterGuestChat(targetChatId);
        }
    } catch (error) {
        console.error(error);
    } finally {
        registeredChatId.value = null;
    }
}

watch(activeChatId, async(newChatId, oldChatId) => {
    if (oldChatId && oldChatId !== newChatId) {
        await unregisterGuestChat(oldChatId);
    }

    if (newChatId && newChatId !== oldChatId) {
        unreadCount.value = 0;
    }

    if (user.value) {
        return;
    }

    if (newChatId) {
        await registerGuestChat(newChatId);
    }
}, { immediate: true });

useBusEvents({
    ChatMessageReceived: message => {
        if (message.chatId !== activeChatId.value) {
            return;
        }

        if (isOpen.value) {
            return;
        }

        const currentUser = user.value;
        if (currentUser && message.senderUserId === currentUser.id) {
            return;
        }

        if (!currentUser) {
            const senderName = message.senderName?.trim().toLowerCase() ?? '';
            if (!message.senderUserId && senderName === normalizedGuestName.value) {
                return;
            }

            if (!message.senderUserId && !normalizedGuestName.value && !senderName) {
                return;
            }
        }

        unreadCount.value += 1;
    },
    SIGNALR_RECONNECT: async() => {
        if (!user.value && activeChatId.value) {
            await registerGuestChat(activeChatId.value, true);
        }
    },
});

onBeforeUnmount(() => {
    void unregisterGuestChat();
});

watch(isOpen, value => {
    if (value) {
        unreadCount.value = 0;
    }
});
</script>

<style lang="scss" scoped>
.floating-chat {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 50;

    &__trigger {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background-color: theme('colors.green.primary');
        color: theme('colors.white');
        border: none;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        position: relative;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
        }

        &:disabled {
            opacity: 0.7;
            cursor: not-allowed;
        }
    }

    &__badge {
        position: absolute;
        top: -4px;
        right: -4px;
        min-width: 20px;
        height: 20px;
        padding: 0 6px;
        border-radius: 9999px;
        background-color: theme('colors.red.700');
        color: theme('colors.white');
        font-size: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    }

    &__panel {
        position: fixed;
        right: 20px;
        bottom: 90px;
        width: 360px;
        max-width: calc(100vw - 40px);
        height: 520px;
        display: flex;
        flex-direction: column;
        background-color: theme('colors.background');
        border-radius: 12px;
        box-shadow: 0 16px 45px rgba(0, 0, 0, 0.18);
        overflow: hidden;

        @screen sm {
            width: 400px;
        }
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        background-color: theme('colors.green.primary');
        color: theme('colors.white');
    }

    &__title {
        font-size: 16px;
        font-weight: 600;
    }

    &__close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border: none;
        background: transparent;
        color: inherit;
        cursor: pointer;
    }

    &__body {
        flex: 1 1 auto;
        display: flex;
        background-color: theme('colors.elements');
        overflow: hidden;
        min-height: 0;
    }

    &__chat {
        flex: 1 1 auto;
        min-height: 0;
    }

    &__status {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1 1 auto;
        font-size: 14px;
        color: theme('colors.black');
    }
}

.floating-chat-slide-enter-from,
.floating-chat-slide-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

.floating-chat-slide-enter-active,
.floating-chat-slide-leave-active {
    transition: all 0.2s ease;
}
</style>
